import React, { useMemo, useState, useEffect } from "react";
import { useDemoPage } from "@/lib/useDemoPage";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, Boxes, Mail, FileText, MapPin, Loader2, ArrowRight, Upload, Info, CheckCircle2, AlertTriangle } from "lucide-react";
import { supabase } from "../lib/supabaseClient";
/**
 * Transport & logistique d'œuvres — Demande de devis
 * -----------------------------------------------------
 * Parcours en 3 étapes pour capter une demande qualifiée et l'enregistrer
 * dans `transport_requests` (status: "pending"). L'assignation au partenaire
 * transporteur actif est gérée côté base par le trigger
 * `assign_partner_transport_request` (BEFORE INSERT sur transport_requests),
 * sur le même principe que pour les demandes d'assurance.
 *
 * Étape 1 : coordonnées + enlèvement/livraison + œuvre(s)
 * Étape 2 : options logistiques (emballage, caisse, température, assurances, douanes)
 * Étape 3 : récap + envoi + confirmation
 *
 * Note : les fichiers joints (étape 2, "Importer") ne sont aujourd'hui stockés
 * que côté navigateur — seuls leurs noms/tailles sont enregistrés dans
 * `attachments`. Si on veut conserver les fichiers eux-mêmes, il faudra les
 * envoyer vers un bucket Supabase Storage avant l'insert (à faire dans un
 * second temps si besoin).
 */

const Field = ({ label, required, hint, children }) => (
  <label className="block">
    <div className="mb-1 text-sm font-medium flex items-center gap-2">
      <span>{label}{required && <span className="text-red-600"> *</span>}</span>
      {hint && <span className="text-xs text-gray-500 inline-flex items-center gap-1"><Info size={14}/> {hint}</span>}
    </div>
    {children}
  </label>
);

const PriceRow = ({ label, amount, bold }) => {
  const { i18n } = useTranslation();
  const language = String(i18n.resolvedLanguage || i18n.language || "fr").toLowerCase();
  const locale = language.startsWith("de") ? "de-DE" : language.startsWith("en") ? "en-US" : "fr-FR";
  return (
    <div className={`flex justify-between ${bold ? "font-semibold" : ""}`}>
      <span>{label}</span>
      <span>{Number(amount || 0).toLocaleString(locale, { style: "currency", currency: "EUR" })}</span>
    </div>
  );
};

function useQuote({ distanceKm, pieces, caisse, tempControle, assurance, volumetrie, international, etage, manipulation }) {
  // Modèle de chiffrage indicatif (estimation rapide affichée au client, pas un devis ferme)
  return useMemo(() => {
    const basePickup = 120; // collecte
    const baseKm = distanceKm * 1.2; // €/km
    const handling = manipulation === "complexe" ? 200 : manipulation === "moyenne" ? 90 : 40;
    const floor = etage > 0 ? etage * 25 : 0;
    const packing = caisse === "bois_musee" ? 220 : caisse === "carton_renforce" ? 80 : 35;
    const packTotal = packing * Math.max(1, pieces);
    const volumeFee = volumetrie === "grande" ? 240 : volumetrie === "moyenne" ? 120 : 40;
    const temp = tempControle ? 160 : 0;
    const intl = international ? 260 : 0;
    const insuranceFee = assurance ? Math.max(35, (assurance * 0.003)) : 0; // 0,3% valeur déclarée

    const sousTotal = basePickup + baseKm + handling + floor + packTotal + volumeFee + temp + intl;
    const assuranceTotal = insuranceFee;
    const tva = 0.2 * (sousTotal + assuranceTotal);
    const total = sousTotal + assuranceTotal + tva;

    return { sousTotal, assuranceTotal, tva, total };
  }, [distanceKm, pieces, caisse, tempControle, assurance, volumetrie, international, etage, manipulation]);
}

export default function TransportPreforum() {
  useDemoPage("transport-preforum", { noindex: true });
  const { t } = useTranslation();
  useEffect(() => {
    document.title = t("transportPreforum.documentTitle");
  }, [t]);
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [savedRequestId, setSavedRequestId] = useState(null);
  const [files, setFiles] = useState([]);
  const { artworkId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    // Transporteur (sélection partenaire)
    transporteur: "Artrans", // Artrans | LP Art | Chenue | Convelio | Autre
    // Client
    civilite: "Mme",
    prenom: "",
    nom: "",
    email: "",
    telephone: "",

    // Lieux & dates
    pickupAdresse: "",
    pickupVille: "",
    pickupPays: "France",
    deliveryAdresse: "",
    deliveryVille: "",
    deliveryPays: "France",
    pickupDate: "",
    deliveryDate: "",
    distanceKm: 30,
    etage: 0,

    // Œuvre(s)
    pieces: 1,
    categorie: "Peinture",
    longueurCm: 80,
    largeurCm: 60,
    hauteurCm: 10,
    poidsKg: 12,
    volumetrie: "moyenne", // petite / moyenne / grande
    manipulation: "moyenne", // simple / moyenne / complexe

    // Options logistiques
    emballage: "film_bulles", // film_bulles / mousse / caisse
    caisse: "carton_renforce", // carton_renforce / bois_musee / aucune
    tempControle: false,
    international: false,

    // Assurance & valeur déclarée
    // (anciennement "assurance" — renommé en assuranceValeur pour matcher
    // le calcul du devis et le champ du formulaire, qui utilisaient déjà ce nom)
    assuranceValeur: 50000,

    // Douanes
    douane: false,
    incoterm: "DAP",

    // Consentement
    consent: true,
  });
  const [loadingPrefill, setLoadingPrefill] = useState(false);
  const [prefillError] = useState(null);

  // ───────────────────────────────────────────────────────────
  // ① Préremplissage owner (nom/prénom/téléphone/email)
  //    - email via RPC sécurisée get_user_email(uid uuid)
  useEffect(() => {
    const prefillOwner = async () => {
      if (!artworkId) return;
      try {
        // on récupère le owner depuis l'oeuvre
        const { data: aw, error: eAw } = await supabase
          .from("artworks")
          .select("id, client_id")
          .eq("id", artworkId)
          .maybeSingle();
        if (eAw || !aw?.client_id) return;

        const ownerId = aw.client_id;

        // profil (prenom/nom/tel)
        const { data: prof } = await supabase
          .from("profiles")
          .select("first_name, last_name, phone")
          .eq("id", ownerId)
          .maybeSingle();

        // email depuis auth.users via RPC
        let email = "";
        const { data: rpcEmail } = await supabase.rpc("get_user_email", { uid: ownerId });
        if (typeof rpcEmail === "string") email = rpcEmail;

        setForm(f => ({
          ...f,
          prenom: prof?.first_name ?? f.prenom,
          nom: prof?.last_name ?? f.nom,
          telephone: prof?.phone ?? f.telephone,
          email: email || f.email,
        }));
      } catch (err) {
        console.warn("Prefill owner error:", err);
      }
    };
    prefillOwner();
  }, [artworkId]);

  // ───────────────────────────────────────────────────────────

  useEffect(() => {
    const prefillPickupFromArtwork = async () => {
      if (!artworkId) return;
      setLoadingPrefill(true);
      try {
        const { data, error } = await supabase.rpc(
          "get_artwork_pickup_address",
          { p_artwork_id: artworkId }
        );
        if (error) throw error;
        if (data && typeof data === "string" && data.trim().length > 0) {
          setForm(f => ({ ...f, pickupAdresse: data.trim() }));
        } else {
          console.info("Aucune adresse pickup renvoyée par la RPC.");
        }
      } catch (e) {
        console.error("Prefill pickup RPC error:", e);
      } finally {
        setLoadingPrefill(false);
      }
    };
    prefillPickupFromArtwork();
  }, [artworkId]);
  const quote = useQuote({
    distanceKm: Number(form.distanceKm||0),
    pieces: Number(form.pieces||1),
    caisse: form.caisse,
    tempControle: form.tempControle,
    assurance: Number(form.assuranceValeur||0),
    volumetrie: form.volumetrie,
    international: form.international,
    etage: Number(form.etage||0),
    manipulation: form.manipulation,
  });

  // Payload "aperçu" lisible (affiché et copiable à l'étape 3, avant envoi)
  const payload = useMemo(() => {
    return {
      partner: {
        name: `ARTKIV × ${form.transporteur}`,
        code: `TRANSPORT_${form.transporteur.replace(/\s+/g, "_").toUpperCase()}`
      },
      client: {
        salutation: form.civilite, first_name: form.prenom, last_name: form.nom, email: form.email, phone: form.telephone,
      },
      logistics: {
        pickup: { address: form.pickupAdresse, city: form.pickupVille, country: form.pickupPays, date: form.pickupDate },
        delivery: { address: form.deliveryAdresse, city: form.deliveryVille, country: form.deliveryPays, date: form.deliveryDate },
        distance_km: Number(form.distanceKm), floor: Number(form.etage)
      },
      objects: [{
        category: form.categorie,
        pieces: Number(form.pieces),
        size_cm: { L: Number(form.longueurCm), l: Number(form.largeurCm), H: Number(form.hauteurCm) },
        weight_kg: Number(form.poidsKg),
        volume: form.volumetrie,
        handling: form.manipulation,
      }],
      options: {
        packing: form.emballage,
        crate: form.caisse,
        temperature_control: form.tempControle,
        international: form.international,
        customs: form.douane ? { enabled: true, incoterm: form.incoterm } : { enabled: false },
      },
      insurance: { declared_value_eur: Number(form.assuranceValeur), premium_preview_eur: quote.assuranceTotal },
      quote_preview: quote,
      meta: { submitted_at_iso: new Date().toISOString() }
    };
  }, [form, quote]);

  // Construit la ligne à insérer dans `transport_requests` (vraies colonnes de la table)
  const buildDbPayload = (userId) => ({
    user_id: userId,
    scope: "single",
    artworks: artworkId ? [artworkId] : [],
    pickup_address: [form.pickupAdresse, form.pickupVille, form.pickupPays].filter(Boolean).join(", "),
    delivery_address: [form.deliveryAdresse, form.deliveryVille, form.deliveryPays].filter(Boolean).join(", "),
    trip_type: "one_way",
    dates: { pickup: form.pickupDate || null, delivery: form.deliveryDate || null },
    services: {
      transporteur_souhaite: form.transporteur,
      packing: form.emballage,
      crate: form.caisse,
      customs: form.douane ? { enabled: true, incoterm: form.incoterm } : { enabled: false },
      insurance: { type: "valeur déclarée", amount_eur: Number(form.assuranceValeur) || null },
    },
    constraints: {
      volumetrie: form.volumetrie,
      manutention: form.manipulation,
      temperature_controlee: !!form.tempControle,
      international: !!form.international,
      etage: Number(form.etage) || 0,
      distance_km: Number(form.distanceKm) || 0,
    },
    packaging_request: null,
    // Pas d'upload réel vers Storage pour l'instant : on garde au moins une trace des noms de fichiers
    attachments: files.map((f) => ({ name: f.name, size: f.size })),
    computed: {
      client: {
        salutation: form.civilite,
        first_name: form.prenom,
        last_name: form.nom,
        email: form.email,
        phone: form.telephone,
      },
      objects: [{
        category: form.categorie,
        pieces: Number(form.pieces) || 1,
        size_cm: { L: Number(form.longueurCm), l: Number(form.largeurCm), H: Number(form.hauteurCm) },
        weight_kg: Number(form.poidsKg) || null,
      }],
      quote_preview: quote,
    },
    partner_id: null, // rempli automatiquement par le trigger si un partenaire transport actif existe
    status: "pending",
    created_by: userId,
  });

  const send = async () => {
    setSendError("");
    setSending(true);
    try {
      const { data: uRes, error: uErr } = await supabase.auth.getUser();
      if (uErr) throw uErr;
      const user = uRes?.user;
      if (!user?.id) throw new Error(t("transportPreforum.authRequired"));

      const dbPayload = buildDbPayload(user.id);

      const { data, error } = await supabase
        .from("transport_requests")
        .insert(dbPayload)
        .select("id")
        .single();

      if (error) throw error;

      setSavedRequestId(data.id);
      setSubmitted(true);
    } catch (e) {
      console.error("Erreur envoi demande transport:", e);
      setSendError(e?.message || t("transportPreforum.sendError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-black text-white grid place-items-center"><Truck size={18}/></div>
            <div>
              <div className="text-xl font-bold">{t("transportPreforum.title")}</div>
              <div className="text-gray-600 text-sm">{t("transportPreforum.subtitle")}</div>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <div className={`rounded-xl p-4 border ${step>=1?"bg-white":"opacity-60"}`}><MapPin className="mb-2"/> {t("transportPreforum.steps.one")}</div>
          <div className={`rounded-xl p-4 border ${step>=2?"bg-white":"opacity-60"}`}><Boxes className="mb-2"/> {t("transportPreforum.steps.two")}</div>
          <div className={`rounded-xl p-4 border ${step>=3?"bg-white":"opacity-60"}`}><Mail className="mb-2"/> {t("transportPreforum.steps.three")}</div>
        </div>
      {loadingPrefill && (
        <div className="text-xs text-gray-500 mb-2">{t("transportPreforum.prefill")}</div>
      )}
      {prefillError && (
        <div className="text-xs text-amber-700 mb-2">{prefillError}</div>
      )}

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-2xl shadow p-5">
            {step===1 && (
              <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}}>
<div className="grid md:grid-cols-3 gap-4">
  <Field label={t("transportPreforum.fields.carrier")} required>
    <select
      className="w-full border rounded-lg px-3 py-2"
      value={form.transporteur}
      onChange={e => setForm({ ...form, transporteur: e.target.value })}
    >
      <option>Artrans</option>
      <option>LP Art</option>
      <option>Chenue</option>
      <option>Convelio</option>
      <option value="Autre">{t("transportPreforum.other")}</option>
    </select>
  </Field>
                  <Field label={t("transportPreforum.fields.salutation")}>
                    <select className="w-full border rounded-lg px-3 py-2" value={form.civilite} onChange={e=>setForm({...form, civilite:e.target.value})}>
                      <option value="Mme">{t("transportPreforum.salutations.mrs")}</option><option value="M.">{t("transportPreforum.salutations.mr")}</option><option value="Autre">{t("transportPreforum.salutations.other")}</option>
                    </select>
                  </Field>
                  <Field label={t("transportPreforum.fields.firstName")} required>
                    <input className="w-full border rounded-lg px-3 py-2" value={form.prenom} onChange={e=>setForm({...form, prenom:e.target.value})}/>
                  </Field>
                  <Field label={t("transportPreforum.fields.lastName")} required>
                    <input className="w-full border rounded-lg px-3 py-2" value={form.nom} onChange={e=>setForm({...form, nom:e.target.value})}/>
                  </Field>
                  <Field label={t("transportPreforum.fields.email")} required>
                    <input type="email" className="w-full border rounded-lg px-3 py-2" value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/>
                  </Field>
                  <Field label={t("transportPreforum.fields.phone")} required>
                    <input className="w-full border rounded-lg px-3 py-2" value={form.telephone} onChange={e=>setForm({...form, telephone:e.target.value})}/>
                  </Field>
                </div>

                <div className="mt-6 grid md:grid-cols-2 gap-4">
                  <Field label={t("transportPreforum.fields.pickupAddress")} required>
	    <input
      		className="w-full border rounded-lg px-3 py-2"
      		value={form.pickupAdresse}
      		onChange={(e)=>setForm({ ...form, pickupAdresse: e.target.value })}
      		placeholder={t("transportPreforum.pickupPlaceholder")}
	    />
                  </Field>
                  <Field label={t("transportPreforum.fields.city")} required>
                    <input className="w-full border rounded-lg px-3 py-2" value={form.pickupVille} onChange={e=>setForm({...form, pickupVille:e.target.value})}/>
                  </Field>
                  <Field label={t("transportPreforum.fields.country")}>
                    <input className="w-full border rounded-lg px-3 py-2" value={form.pickupPays} onChange={e=>setForm({...form, pickupPays:e.target.value})}/>
                  </Field>
                  <Field label={t("transportPreforum.fields.desiredDate")}>
                    <input type="date" className="w-full border rounded-lg px-3 py-2" value={form.pickupDate} onChange={e=>setForm({...form, pickupDate:e.target.value})}/>
                  </Field>
                </div>

                <div className="mt-6 grid md:grid-cols-2 gap-4">
                  <Field label={t("transportPreforum.fields.deliveryAddress")} required>
                    <input className="w-full border rounded-lg px-3 py-2" value={form.deliveryAdresse} onChange={e=>setForm({...form, deliveryAdresse:e.target.value})}/>
                  </Field>
                  <Field label={t("transportPreforum.fields.city")} required>
                    <input className="w-full border rounded-lg px-3 py-2" value={form.deliveryVille} onChange={e=>setForm({...form, deliveryVille:e.target.value})}/>
                  </Field>
                  <Field label={t("transportPreforum.fields.country")}>
                    <input className="w-full border rounded-lg px-3 py-2" value={form.deliveryPays} onChange={e=>setForm({...form, deliveryPays:e.target.value})}/>
                  </Field>
                  <Field label={t("transportPreforum.fields.desiredDate")}>
                    <input type="date" className="w-full border rounded-lg px-3 py-2" value={form.deliveryDate} onChange={e=>setForm({...form, deliveryDate:e.target.value})}/>
                  </Field>
                </div>

                <div className="mt-6 grid md:grid-cols-4 gap-4">
                  <Field label={t("transportPreforum.fields.distance")} hint={t("transportPreforum.hintApprox")}>
                    <input type="number" className="w-full border rounded-lg px-3 py-2" value={form.distanceKm} onChange={e=>setForm({...form, distanceKm:e.target.value})}/>
                  </Field>
                  <Field label={t("transportPreforum.fields.floor")}>
                    <input type="number" className="w-full border rounded-lg px-3 py-2" value={form.etage} onChange={e=>setForm({...form, etage:e.target.value})}/>
                  </Field>
                  <Field label={t("transportPreforum.fields.pieces")}>
                    <input type="number" className="w-full border rounded-lg px-3 py-2" value={form.pieces} onChange={e=>setForm({...form, pieces:e.target.value})}/>
                  </Field>
                  <Field label={t("transportPreforum.fields.category")}>
                    <select className="w-full border rounded-lg px-3 py-2" value={form.categorie} onChange={e=>setForm({...form, categorie:e.target.value})}>
                      <option value="Peinture">{t("transportPreforum.categories.painting")}</option><option value="Sculpture">{t("transportPreforum.categories.sculpture")}</option><option value="Dessin / Papier">{t("transportPreforum.categories.paper")}</option><option value="Photographie">{t("transportPreforum.categories.photography")}</option><option value="Mobilier / Objet">{t("transportPreforum.categories.object")}</option>
                    </select>
                  </Field>
                </div>

                <div className="mt-6 grid md:grid-cols-4 gap-4">
                  <Field label={t("transportPreforum.fields.length")}><input type="number" className="w-full border rounded-lg px-3 py-2" value={form.longueurCm} onChange={e=>setForm({...form, longueurCm:e.target.value})}/></Field>
                  <Field label={t("transportPreforum.fields.width")}><input type="number" className="w-full border rounded-lg px-3 py-2" value={form.largeurCm} onChange={e=>setForm({...form, largeurCm:e.target.value})}/></Field>
                  <Field label={t("transportPreforum.fields.height")}><input type="number" className="w-full border rounded-lg px-3 py-2" value={form.hauteurCm} onChange={e=>setForm({...form, hauteurCm:e.target.value})}/></Field>
                  <Field label={t("transportPreforum.fields.weight")}><input type="number" className="w-full border rounded-lg px-3 py-2" value={form.poidsKg} onChange={e=>setForm({...form, poidsKg:e.target.value})}/></Field>
                </div>


                <div className="mt-6 flex justify-between">
                  {artworkId && (
                    <button
                      type="button"
                      onClick={() => navigate(`/artworks/${artworkId}`)}
                      className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-all"
                    >
                      {t("transportPreforum.cancel")}
                    </button>
                  )}
                  <button
                    onClick={() => setStep(2)}
                    className="px-4 py-2 rounded-lg bg-black text-white inline-flex items-center gap-2 ml-auto"
                  >
                    {t("transportPreforum.continue")} <ArrowRight size={16}/>
                  </button>
                </div>
              </motion.div>
            )}

            {step===2 && (
              <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}}>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Badge estimation */}
                  <div className="md:col-span-2 mb-1">
                    <div className="inline-flex items-center gap-2 text-xs px-2 py-1 rounded-md bg-gray-100 text-gray-800">
                      <span>{t("transportPreforum.artkiv")}</span>
                      <span className="text-gray-500">— {t("transportPreforum.indicative")}</span>
                    </div>
                  </div>
                  <Field label={t("transportPreforum.fields.packing")}>
                    <select className="w-full border rounded-lg px-3 py-2" value={form.emballage} onChange={e=>setForm({...form, emballage:e.target.value})}>
                      <option value="film_bulles">{t("transportPreforum.packing.bubble")}</option>
                      <option value="mousse">{t("transportPreforum.packing.foam")}</option>
                      <option value="caisse">{t("transportPreforum.packing.crate")}</option>
                    </select>
                  </Field>
                  <Field label={t("transportPreforum.fields.crate")}>
                    <select className="w-full border rounded-lg px-3 py-2" value={form.caisse} onChange={e=>setForm({...form, caisse:e.target.value})}>
                      <option value="aucune">{t("transportPreforum.crate.none")}</option>
                      <option value="carton_renforce">{t("transportPreforum.crate.cardboard")}</option>
                      <option value="bois_musee">{t("transportPreforum.crate.museum")}</option>
                    </select>
                  </Field>
                  <Field label={t("transportPreforum.fields.volume")}>
                    <select className="w-full border rounded-lg px-3 py-2" value={form.volumetrie} onChange={e=>setForm({...form, volumetrie:e.target.value})}>
                      <option value="petite">{t("transportPreforum.volume.small")}</option>
                      <option value="moyenne">{t("transportPreforum.volume.medium")}</option>
                      <option value="grande">{t("transportPreforum.volume.large")}</option>
                    </select>
                  </Field>
                  <Field label={t("transportPreforum.fields.handling")}>
                    <select className="w-full border rounded-lg px-3 py-2" value={form.manipulation} onChange={e=>setForm({...form, manipulation:e.target.value})}>
                      <option value="simple">{t("transportPreforum.handling.simple")}</option>
                      <option value="moyenne">{t("transportPreforum.handling.medium")}</option>
                      <option value="complexe">{t("transportPreforum.handling.complex")}</option>
                    </select>
                  </Field>
                  <Field label={t("transportPreforum.fields.international")}>
                    <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={form.international} onChange={e=>setForm({...form, international:e.target.checked})}/> {t("transportPreforum.yes")}</label>
                  </Field>
                  <Field label={t("transportPreforum.fields.temperature")}>
                    <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={form.tempControle} onChange={e=>setForm({...form, tempControle:e.target.checked})}/> {t("transportPreforum.yes")}</label>
                  </Field>
                  <Field label={t("transportPreforum.fields.declaredValue")}>
                    <input type="number" className="w-full border rounded-lg px-3 py-2" value={form.assuranceValeur} onChange={e=>setForm({...form, assuranceValeur:e.target.value})}/>
                  </Field>
                  <Field label={t("transportPreforum.fields.customs")}>
                    <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={form.douane} onChange={e=>setForm({...form, douane:e.target.checked})}/> {t("transportPreforum.toPlan")}</label>
                    {form.douane && (
                      <div className="mt-2"><select className="w-full border rounded-lg px-3 py-2" value={form.incoterm} onChange={e=>setForm({...form, incoterm:e.target.value})}><option>DAP</option><option>DDP</option><option>EXW</option></select></div>
                    )}
                  </Field>
                  <Field label={t("transportPreforum.fields.attachments")}>
                    <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg w-fit cursor-pointer"><Upload size={16}/> {t("transportPreforum.import")}<input className="hidden" type="file" multiple onChange={(e)=>setFiles(Array.from(e.target.files||[]))}/></label>
                    <div className="text-xs text-gray-500 mt-1">{files.length ? t("transportPreforum.filesAdded", { count: files.length }) : "—"}</div>
                  </Field>
                </div>

                <div className="mt-6 bg-gray-50 rounded-xl p-4">
                  <div className="text-sm text-gray-600 mb-2">{t("transportPreforum.quickEstimate")}</div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <PriceRow label={t("transportPreforum.quote.subtotal")} amount={quote.sousTotal}/>
                    <PriceRow label={t("transportPreforum.quote.insurance")} amount={quote.assuranceTotal}/>
                    <PriceRow label={t("transportPreforum.quote.vat")} amount={quote.tva}/>
                    <PriceRow label={t("transportPreforum.quote.estimatedTotal")} amount={quote.total} bold/>
                  </div>
                </div>


                <div className="mt-6 flex justify-between">
                  <div className="flex gap-2">
                    {artworkId && (
                      <button
                        type="button"
                        onClick={() => navigate(`/artworks/${artworkId}`)}
                        className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-all"
                      >
                        {t("transportPreforum.cancel")}
                      </button>
                    )}
                    <button onClick={() => setStep(1)} className="px-4 py-2 rounded-lg border">{t("transportPreforum.back")}</button>
                  </div>
                  <button onClick={() => setStep(3)} className="px-4 py-2 rounded-lg bg-black text-white inline-flex items-center gap-2">
                    {t("transportPreforum.continue")} <ArrowRight size={16}/>
                  </button>
                </div>
              </motion.div>
            )}

            {step===3 && !submitted && (
              <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-black text-white grid place-items-center"><Mail/></div>
                  <div>
                    <div className="font-semibold text-lg mb-1">{t("transportPreforum.readyTitle")}</div>
                    <div className="text-gray-600">{t("transportPreforum.readyBody")}</div>
                  </div>
                </div>

                {sendError && (
                  <div className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <AlertTriangle size={16} className="mt-0.5 flex-shrink-0"/>
                    <span>{sendError}</span>
                  </div>
                )}

                <div className="mt-6">
                  <div className="text-sm font-medium mb-2">{t("transportPreforum.summaryRequest")}</div>
                  <pre className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-auto max-h-72 text-xs">{JSON.stringify(payload, null, 2)}</pre>
                  <div className="mt-2 flex justify-between">
                    <div className="flex gap-2">
                      {artworkId && (
                        <button
                          type="button"
                          onClick={() => navigate(`/artworks/${artworkId}`)}
                          className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-2"
                        >
                          {t("transportPreforum.cancel")}
                        </button>
                      )}
                      <button onClick={() => navigator.clipboard.writeText(JSON.stringify(payload, null, 2))} className="px-3 py-2 rounded-lg border inline-flex items-center gap-2">
                        <FileText size={16}/> {t("transportPreforum.copy")}
                      </button>
                    </div>
                    <button onClick={send} disabled={sending} className="px-3 py-2 rounded-lg text-white bg-black inline-flex items-center gap-2 disabled:opacity-50">
                      {sending ? (<><Loader2 className="animate-spin" size={16}/> {t("transportPreforum.sending")}</>) : (<>{t("transportPreforum.send")}</>)}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {step===3 && submitted && (
              <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-600 text-white grid place-items-center"><CheckCircle2/></div>
                  <div>
                    <div className="font-semibold text-lg mb-1">{t("transportPreforum.submittedTitle")}</div>
                    <div className="text-gray-600">
                      {t("transportPreforum.submittedBody", { reference: savedRequestId ? t("transportPreforum.reference", { id: savedRequestId.slice(0, 8) }) : "" })}
                    </div>
                  </div>
                </div>
                <div className="mt-6">
                  {artworkId && (
                    <button
                      type="button"
                      onClick={() => navigate(`/artworks/${artworkId}`)}
                      className="px-4 py-2 rounded-lg bg-black text-white inline-flex items-center gap-2"
                    >
                      {t("transportPreforum.backArtwork")}
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow p-5">
              <div className="font-semibold mb-2">{t("transportPreforum.summary")}</div>
              <div className="text-sm text-gray-700 space-y-1">
		<div>{t("transportPreforum.partner")} <b>{form.transporteur}</b></div>
                <div>{form.civilite} {form.prenom} {form.nom}</div>
                <div className="text-gray-500">{form.email} · {form.telephone}</div>
              </div>
              <div className="mt-3 text-sm">
                <div className="font-medium">{t("transportPreforum.route")}</div>
                <div className="text-gray-600">{form.pickupVille || "—"} → {form.deliveryVille || "—"} · {form.distanceKm} km</div>
                <div className="text-gray-600">{t("transportPreforum.floorLabel")} {form.etage}</div>
              </div>
              <div className="mt-3 text-sm">
                <div className="font-medium">{t("transportPreforum.artworks")}</div>
                <div className="text-gray-600">{form.pieces}× {form.categorie} · {form.longueurCm}×{form.largeurCm}×{form.hauteurCm} cm · {form.poidsKg} kg</div>
              </div>
              {/* À partir de l'étape 2 seulement : options + estimation */}
              {step >= 2 && (
                <>
                  <div className="mt-3 text-sm">
                    <div className="font-medium">{t("transportPreforum.selectedOptions")}</div>
                    <ul className="text-gray-600 list-disc pl-5 space-y-1">
                      <li>{t("transportPreforum.packingLabel")} {form.emballage === "film_bulles" ? t("transportPreforum.packing.bubble") : form.emballage === "mousse" ? t("transportPreforum.packing.foam") : t("transportPreforum.packing.crate")}</li>
                      <li>{t("transportPreforum.crateLabel")} {form.caisse === "aucune" ? t("transportPreforum.crate.none") : form.caisse === "carton_renforce" ? t("transportPreforum.crate.cardboard") : t("transportPreforum.crate.museum")}</li>
                      <li>{t("transportPreforum.volumeLabel")} {t(`transportPreforum.volume.${form.volumetrie === "petite" ? "small" : form.volumetrie === "grande" ? "large" : "medium"}`)}</li>
                      <li>{t("transportPreforum.handlingLabel")} {t(`transportPreforum.handling.${form.manipulation === "simple" ? "simple" : form.manipulation === "complexe" ? "complex" : "medium"}`)}</li>
                      <li>{t("transportPreforum.temperatureLabel")} {form.tempControle ? t("transportPreforum.activeControl") : t("transportPreforum.no")}</li>
                      <li>{t("transportPreforum.internationalLabel")} {form.international ? t("transportPreforum.yes") : t("transportPreforum.no")}</li>
                      {form.douane && <li>{t("transportPreforum.customsLabel", { incoterm: form.incoterm })}</li>}
                    </ul>
                  </div>
                  <div className="mt-4 bg-gray-50 rounded-xl p-3">
                    <div className="text-xs text-gray-600 mb-1">{t("transportPreforum.quote.partnerEstimate")}</div>
                    <PriceRow label={t("transportPreforum.quote.estimatedTotal")} amount={quote.total} bold/>
                  </div>
                </>
              )}
            </div>
        </div>
        </div>
      </div>
    </div>
  );
}